import { supabase } from './supabase';

/**
 * User Qualifications Service
 * Handles user-level qualifications that sync across spaces via course_link_id and course_link_type
 */
export const userQualificationsService = {
  /**
   * Create or update a user qualification
   * This is the single source of truth for a user's qualification status
   *
   * @param {Object} params
   * @param {string} params.userId - User profile ID
   * @param {string} params.requirementId - Compliance requirement ID
   * @param {string} params.status - Qualification status (pending, completed, expired, etc.)
   * @param {string} params.completedDate - When the qualification was completed
   * @param {string} params.expiryDate - When the qualification expires
   * @param {string} params.evidenceUrl - URL to certificate/document
   * @param {string} params.certificateNumber - Certificate number
   * @param {string} params.notes - Additional notes
   * @param {string} params.assignedBy - User ID who assigned/updated this
   * @param {string} params.overrideReason - Override reason ('awaiting_certificate' | 'no_evidence_required' | null)
   * @param {string} params.overrideBy - User ID who performed the override
   * @param {string} params.overrideDate - When the override was applied
   */
  async upsertUserQualification({
    userId,
    requirementId,
    status,
    completedDate = null,
    expiryDate = null,
    evidenceUrl = null,
    certificateNumber = null,
    notes = null,
    assignedBy = null,
    overrideReason = null,
    overrideBy = null,
    overrideDate = null,
    evidenceFiles = undefined
  }) {
    try {
      // Get the requirement to extract course link information
      const { data: requirement, error: reqError } = await supabase
        .from('compliance_requirements')
        .select('id, name, course_link_id, course_link_type')
        .eq('id', requirementId)
        .single();

      if (reqError) throw reqError;
      if (!requirement) throw new Error('Requirement not found');

      // Extract course link data from columns
      const courseLinkId = requirement.course_link_id || null;
      const courseLinkType = requirement.course_link_type || null;

      // Validate course links exist for cross-space sync
      if (!courseLinkId || !courseLinkType) {
        console.warn(
          `Requirement "${requirement.name}" has no course_link_id/type - will not sync across spaces`
        );
      }

      // When evidenceFiles is provided, sync evidence_url from the primary file
      let effectiveEvidenceUrl = evidenceUrl;
      let effectiveEvidenceFiles = undefined;
      if (evidenceFiles !== undefined) {
        effectiveEvidenceFiles = evidenceFiles;
        const primaryFile = Array.isArray(evidenceFiles)
          ? evidenceFiles.find(f => f.is_primary) || evidenceFiles[0]
          : null;
        if (primaryFile) {
          effectiveEvidenceUrl = primaryFile.url;
        }
      }

      // Prepare update fields (no user_id/requirement_id — those are match keys)
      const updateFields = {
        course_link_id: courseLinkId,
        course_link_type: courseLinkType,
        status: status,
        completed_date: completedDate,
        expiry_date: expiryDate,
        evidence_url: effectiveEvidenceUrl,
        certificate_number: certificateNumber,
        notes: notes,
        assigned_by: assignedBy,
        override_reason: overrideReason || null,
        override_by: overrideBy || null,
        override_date: overrideDate || null,
        updated_date: new Date().toISOString()
      };

      // Only include evidence_files if explicitly provided (don't overwrite with undefined)
      if (effectiveEvidenceFiles !== undefined) {
        updateFields.evidence_files = effectiveEvidenceFiles;
      }

      // Check if a row already exists for this user + requirement
      const { data: existingByReq } = await supabase
        .from('user_qualifications')
        .select('id, course_link_id, course_link_type')
        .eq('user_id', userId)
        .eq('requirement_id', requirementId)
        .maybeSingle();

      // Check if a row exists for the same course link (from another space)
      let existingByCourse = null;
      if (courseLinkId && courseLinkType) {
        const { data: courseRow } = await supabase
          .from('user_qualifications')
          .select('id, requirement_id')
          .eq('user_id', userId)
          .eq('course_link_id', courseLinkId)
          .eq('course_link_type', courseLinkType)
          .maybeSingle();
        existingByCourse = courseRow;
      }

      let data;

      if (existingByReq && existingByCourse && existingByReq.id !== existingByCourse.id) {
        // Two separate rows exist: one matched by requirement (missing course_link),
        // one matched by course_link (from another space). Merge them:
        // update the course-linked row with the new data, delete the orphan.
        const { data: updated, error: updateError } = await supabase
          .from('user_qualifications')
          .update({ ...updateFields, requirement_id: requirementId })
          .eq('id', existingByCourse.id)
          .select()
          .single();

        if (updateError) throw updateError;

        // Remove the orphaned requirement-only row
        await supabase
          .from('user_qualifications')
          .delete()
          .eq('id', existingByReq.id);

        data = updated;
      } else if (existingByReq) {
        // Row exists for this requirement — update it, backfill course_link
        const { data: updated, error: updateError } = await supabase
          .from('user_qualifications')
          .update(updateFields)
          .eq('id', existingByReq.id)
          .select()
          .single();

        if (updateError) throw updateError;
        data = updated;
      } else {
        // No existing row for this requirement — upsert (may match by course_link
        // for cross-space sync, or insert fresh)
        const qualificationData = {
          user_id: userId,
          requirement_id: requirementId,
          ...updateFields
        };

        const upsertOptions =
          courseLinkId && courseLinkType
            ? { onConflict: 'user_id,course_link_id,course_link_type' }
            : { onConflict: 'user_id,requirement_id' };

        const { data: upserted, error: upsertError } = await supabase
          .from('user_qualifications')
          .upsert(qualificationData, upsertOptions)
          .select()
          .single();

        if (upsertError) throw upsertError;
        data = upserted;
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error upserting user qualification:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get all qualifications for a user
   *
   * @param {string} userId - User profile ID
   */
  async getUserQualifications(userId) {
    try {
      const { data, error } = await supabase
        .from('user_qualifications')
        .select(
          `
          *,
          requirement:compliance_requirements(*)
        `
        )
        .eq('user_id', userId)
        .order('updated_date', { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error loading user qualifications:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  /**
   * Check if a user has a qualification for a specific course
   * (Used when assigning requirements to check if already qualified)
   *
   * @param {string} userId - User profile ID
   * @param {string} courseLinkId - Course link ID
   * @param {string} courseLinkType - Course link type (verified/custom)
   */
  async checkUserQualification(userId, courseLinkId, courseLinkType) {
    try {
      const { data, error } = await supabase
        .from('user_qualifications')
        .select('*')
        .eq('user_id', userId)
        .eq('course_link_id', courseLinkId)
        .eq('course_link_type', courseLinkType)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = not found

      return { success: true, data, exists: !!data };
    } catch (error) {
      console.error('Error checking user qualification:', error);
      return { success: false, error: error.message, exists: false };
    }
  },

  /**
   * Delete a user qualification
   *
   * @param {string} qualificationId - User qualification ID
   */
  /**
   * Build an evidence_files array entry
   */
  buildEvidenceFileEntry({ url, fileName, fileType, uploadedBy, isPrimary = false }) {
    return {
      url,
      file_name: fileName,
      file_type: fileType || 'unknown',
      uploaded_at: new Date().toISOString(),
      uploaded_by: uploadedBy || null,
      is_primary: isPrimary
    };
  },

  /**
   * Add a file to an existing qualification's evidence_files
   */
  async addEvidenceFile(qualificationId, fileEntry) {
    try {
      const { data: existing, error: fetchError } = await supabase
        .from('user_qualifications')
        .select('evidence_files, evidence_url')
        .eq('id', qualificationId)
        .single();

      if (fetchError) throw fetchError;

      const currentFiles = Array.isArray(existing.evidence_files) ? existing.evidence_files : [];
      // If this is the first file, mark it primary
      if (currentFiles.length === 0) {
        fileEntry.is_primary = true;
      }
      const updatedFiles = [...currentFiles, fileEntry];

      const primaryFile = updatedFiles.find(f => f.is_primary) || updatedFiles[0];
      const { data, error } = await supabase
        .from('user_qualifications')
        .update({
          evidence_files: updatedFiles,
          evidence_url: primaryFile.url,
          updated_date: new Date().toISOString()
        })
        .eq('id', qualificationId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error adding evidence file:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Remove a file from an existing qualification's evidence_files by URL
   */
  async removeEvidenceFile(qualificationId, fileUrl) {
    try {
      const { data: existing, error: fetchError } = await supabase
        .from('user_qualifications')
        .select('evidence_files')
        .eq('id', qualificationId)
        .single();

      if (fetchError) throw fetchError;

      const currentFiles = Array.isArray(existing.evidence_files) ? existing.evidence_files : [];
      const removedFile = currentFiles.find(f => f.url === fileUrl);
      let updatedFiles = currentFiles.filter(f => f.url !== fileUrl);

      // If we removed the primary, promote the first remaining file
      if (removedFile?.is_primary && updatedFiles.length > 0) {
        updatedFiles = updatedFiles.map((f, i) => i === 0 ? { ...f, is_primary: true } : f);
      }

      const primaryFile = updatedFiles.find(f => f.is_primary) || updatedFiles[0];
      const { data, error } = await supabase
        .from('user_qualifications')
        .update({
          evidence_files: updatedFiles,
          evidence_url: primaryFile?.url || null,
          updated_date: new Date().toISOString()
        })
        .eq('id', qualificationId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error removing evidence file:', error);
      return { success: false, error: error.message };
    }
  },

  async deleteUserQualification(qualificationId) {
    try {
      const { error } = await supabase
        .from('user_qualifications')
        .delete()
        .eq('id', qualificationId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting user qualification:', error);
      return { success: false, error: error.message };
    }
  }
};

export default userQualificationsService;
